import { DOCS_OVERLAY_MAX_RATIO, DOCS_PUSH_MAX_RATIO, RESIZE_HANDLE_WIDTH_PX } from '../constants';

export const getDocsLayoutMetrics = (viewportWidthPx: number) => {
  const vw = Math.max(1, viewportWidthPx);
  const pushMaxPx = Math.floor(vw * DOCS_PUSH_MAX_RATIO);
  const overlayMaxPx = Math.floor(vw * DOCS_OVERLAY_MAX_RATIO);
  const minPanelPx = Math.min(320, pushMaxPx);
  return { pushMaxPx, overlayMaxPx, minPanelPx };
};

export const clampSidebarPanelWidth = (panelWidthPx: number, viewportWidthPx: number): number => {
  const { overlayMaxPx, minPanelPx } = getDocsLayoutMetrics(viewportWidthPx);
  return Math.min(overlayMaxPx, Math.max(minPanelPx, panelWidthPx));
};

export type DocsLayoutSnapshot = {
  sidebarWidthPx: number;
  isOverlayMode: boolean;
  dockWidthPx: number;
  innerWidthPx: number;
  innerMarginLeftPx: number;
};

/** Derives dock/sidebar dimensions for push vs overlay modes. */
export const computeDocsLayout = (
  viewportWidthPx: number,
  sidebarWidthPx: number,
  isOpen: boolean
): DocsLayoutSnapshot => {
  const { pushMaxPx } = getDocsLayoutMetrics(viewportWidthPx);
  const panelWidth = clampSidebarPanelWidth(sidebarWidthPx, viewportWidthPx);
  const isOverlayMode = panelWidth > pushMaxPx;
  const dockWidthPx = isOpen ? Math.min(panelWidth, pushMaxPx) + RESIZE_HANDLE_WIDTH_PX : 0;
  const innerWidthPx = panelWidth + RESIZE_HANDLE_WIDTH_PX;
  const innerMarginLeftPx = dockWidthPx - innerWidthPx;

  return {
    sidebarWidthPx: panelWidth,
    isOverlayMode,
    dockWidthPx,
    innerWidthPx,
    innerMarginLeftPx,
  };
};

export type DocsDockElements = {
  outer: HTMLElement | null;
  inner: HTMLElement | null;
  sidebar: HTMLElement | null;
};

/** Applies layout directly to DOM — used during resize to avoid React re-renders. */
export const applyDocsDockLayout = (
  elements: DocsDockElements,
  layout: DocsLayoutSnapshot,
  overlayBoxShadow?: string
): void => {
  if (elements.outer) {
    elements.outer.style.width = `${layout.dockWidthPx}px`;
    elements.outer.style.zIndex = layout.isOverlayMode ? '20' : '1';
  }
  if (elements.inner) {
    elements.inner.style.width = `${layout.innerWidthPx}px`;
    elements.inner.style.minWidth = `${layout.innerWidthPx}px`;
    elements.inner.style.marginLeft = `${layout.innerMarginLeftPx}px`;
    elements.inner.style.boxShadow = layout.isOverlayMode && overlayBoxShadow ? overlayBoxShadow : '';
  }
  if (elements.sidebar) {
    elements.sidebar.style.width = `${layout.sidebarWidthPx}px`;
  }
};

export const disableDocsDockTransitions = (elements: DocsDockElements): void => {
  if (elements.outer) elements.outer.style.transition = 'none';
  if (elements.inner) elements.inner.style.transition = 'none';
};

export const clearDocsDockInlineLayout = (elements: DocsDockElements): void => {
  if (elements.outer) {
    elements.outer.style.width = '';
    elements.outer.style.zIndex = '';
    elements.outer.style.transition = '';
  }
  if (elements.inner) {
    elements.inner.style.width = '';
    elements.inner.style.minWidth = '';
    elements.inner.style.marginLeft = '';
    elements.inner.style.boxShadow = '';
    elements.inner.style.transition = '';
  }
  if (elements.sidebar) {
    elements.sidebar.style.width = '';
  }
};
